import { useAuth } from "@dl/web-core-js/auth";
import { PageHeader } from "@dl/web-core-js/shell";
import { Card } from "@dl/web-core-js/ui";
import { useTranslation } from "react-i18next";
import type { ReactNode } from "react";

/**
 * A settings screen made of panels rather than one full-bleed grid.
 *
 * These tables are tiny: pipeline settings holds one row, the currency rates
 * two, the client pricing reference period one. A grid gives each of them a
 * whole page of empty space, a filter row, a column menu and sorting, for data
 * that would fit in a paragraph. Panels put several of them on one page and
 * leave room for a heading that says what each is for.
 *
 * The page is the unit of access: one view code covers every panel on it, so
 * the panels themselves carry no permission.
 */
export function SettingsPage({
  title,
  subtitle,
  view,
  children,
}: {
  title: string;
  subtitle?: string;
  view: string;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const { hasView } = useAuth();

  if (!hasView(view)) {
    return (
      <>
        <PageHeader title={title} />
        <div className="p-4 lg:p-6">
          <p className="text-[13px] text-ink-muted">
            {t("error.access-denied.detail", "Your account does not have permission to view this page.")}
          </p>
        </div>
      </>
    );
  }

  return (
    <>
      <PageHeader title={title} {...(subtitle ? { subtitle } : {})} />
      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4 lg:p-6">{children}</div>
    </>
  );
}

/** One titled block, gated by the page it sits on. */
export function SettingsPanel({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: ReactNode;
}) {
  return (
    <Card className="p-5">
      <h2 className="text-[14px] font-semibold text-ink">{title}</h2>
      {description && <p className="mt-0.5 text-[12px] text-ink-muted">{description}</p>}
      <div className="mt-4">{children}</div>
    </Card>
  );
}

/** A panel whose content is a small grid, which needs a bounded height. */
export function GridPanel({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: ReactNode;
}) {
  return (
    <SettingsPanel title={title} {...(description ? { description } : {})}>
      <div className="flex h-80 flex-col overflow-hidden rounded-md border border-line">
        {children}
      </div>
    </SettingsPanel>
  );
}
